import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';

export interface SubscriptionPlan {
  code: string;
  name: string;
  min_patients: number;
  max_patients: number | null;
  max_file_uploads: number | null;
  price_cents: number;
  features: string[];
}

interface SubscriptionState {
  // Estado do plano atual
  currentPlan: SubscriptionPlan | null;
  patientsCount: number;
  loading: boolean;
  
  // Métodos
  loadSubscription: () => Promise<void>;
  refreshPatientsCount: () => Promise<void>;
  
  // Helpers
  canAddPatient: () => boolean;
  canUploadMultipleFiles: (fileCount: number) => boolean;
  getMaxFileUploads: () => number | null;
  getMaxPatients: () => number | null;
  isFree: () => boolean;
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  currentPlan: null,
  patientsCount: 0,
  loading: true,

  loadSubscription: async () => {
    try {
      set({ loading: true });
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ loading: false, currentPlan: null, patientsCount: 0 });
        return;
      }

      // Buscar assinatura válida (ativa, futura ou pendente - não cancelada/suspensa)
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('plan_code, status')
        .eq('user_id', user.id)
        .in('status', ['active', 'future', 'pending'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const { data: profile } = await supabase
        .from('profiles')
        .select('current_plan_code')
        .eq('id', user.id)
        .maybeSingle();

      // Pending = pagamento ainda não confirmado pelo webhook Pagar.me - não exibir como upgrade
      const isPendingPayment = subscription?.status === 'pending';
      let planCode = isPendingPayment ? null : subscription?.plan_code;

      // Se não encontrou assinatura válida ou está pending, usar o current_plan_code do profile
      if (!planCode) {
        planCode = profile?.current_plan_code || 'free';
        
        // Se encontrou um plano no profile que não é 'free', verificar se existe assinatura
        // (pode estar em status diferente de active/future/pending)
        if (planCode && planCode !== 'free') {
          const { data: anySubscription } = await supabase
            .from('subscriptions')
            .select('plan_code, status')
            .eq('user_id', user.id)
            .eq('plan_code', planCode)
            .neq('status', 'canceled')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          
          // Se não encontrou assinatura válida para esse plano, voltar para free
          if (!anySubscription) {
            planCode = 'free';
          }
        }
      } else {
        planCode = planCode || 'free';
      }

      // Buscar plano atual
      const { data: plan } = await supabase
        .from('subscription_plans')
        .select('code, name, min_patients, max_patients, max_file_uploads, price_cents, features')
        .eq('code', planCode)
        .single();

      if (!plan) {
        // Plano padrão FREE se não encontrar
        set({
          currentPlan: {
            code: 'free',
            name: 'Plano Gratuito',
            min_patients: 0,
            max_patients: 10,
            max_file_uploads: 1,
            price_cents: 0,
            features: [],
          },
          loading: false,
        });
        await get().refreshPatientsCount();
        return;
      }

      // Contar pacientes ativos
      const { count } = await supabase
        .from('patients_onboarding')
        .select('*', { count: 'exact', head: true })
        .eq('owner_id', user.id)
        .eq('status', 'active');

      const patientsCount = count || 0;

      set({
        currentPlan: {
          code: plan.code,
          name: plan.name,
          min_patients: plan.min_patients,
          max_patients: plan.max_patients,
          max_file_uploads: plan.max_file_uploads ?? null,
          price_cents: plan.price_cents,
          features: Array.isArray(plan.features) ? plan.features as string[] : [],
        },
        patientsCount,
        loading: false,
      });
    } catch (error) {
      console.error('Error loading subscription:', error);
      set({ loading: false });
    }
  },

  refreshPatientsCount: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { count } = await supabase
        .from('patients_onboarding')
        .select('*', { count: 'exact', head: true })
        .eq('owner_id', user.id)
        .eq('status', 'active');

      set({ patientsCount: count || 0 });
    } catch (error) {
      console.error('Error refreshing patients count:', error);
    }
  },

  canAddPatient: () => {
    const { currentPlan, patientsCount } = get();
    if (!currentPlan) return false;
    
    // Se max_patients é null, significa ilimitado
    if (currentPlan.max_patients === null) return true;
    
    return patientsCount < currentPlan.max_patients;
  },

  canUploadMultipleFiles: (fileCount: number) => {
    const { currentPlan } = get();
    if (!currentPlan) return false;
    
    // Se max_file_uploads é null, significa ilimitado
    if (currentPlan.max_file_uploads === null) return true;
    
    // Se max_file_uploads é 1, não permite múltiplos arquivos
    if (currentPlan.max_file_uploads === 1) return fileCount <= 1;
    
    return fileCount <= currentPlan.max_file_uploads;
  },

  getMaxFileUploads: () => {
    const { currentPlan } = get();
    return currentPlan?.max_file_uploads ?? null;
  },

  getMaxPatients: () => {
    const { currentPlan } = get();
    return currentPlan?.max_patients ?? null;
  },

  isFree: () => {
    const { currentPlan } = get();
    return currentPlan?.code === 'free';
  },
}));

